import { Button, Input } from '@heroui/react'
import { useState, useRef, useEffect, useMemo } from 'react'
import { email as emailSchema, FIELD_LIMITS } from '@leangap/types'

import Header from '@/components/public/Header'
import { Mail } from 'lucide-react'

export default function EmailForm({
	onSubmit,
	sending,
	serverError,
}: {
	onSubmit: (email: string) => void
	sending: boolean
	serverError?: string | null
}) {
	const [email, setEmail] = useState('')
	const [touched, setTouched] = useState(false)
	const inputRef = useRef<HTMLInputElement | null>(null)

	// Validate email with TLD check (same schema as server)
	const validation = useMemo(() => {
		const trimmed = email.trim()
		if (!trimmed) return { valid: false, error: null } // empty = no error shown, but can't submit
		const result = emailSchema.safeParse(trimmed)
		if (result.success) return { valid: true, error: null }
		return {
			valid: false,
			error: result.error.issues[0]?.message || 'Please enter a valid email address',
		}
	}, [email])

	// Show error if: touched and has local validation error, OR server returned an error
	const displayError = touched && validation.error ? validation.error : serverError
	const canSend = validation.valid

	useEffect(() => {
		// Guard SSR
		if (typeof window === 'undefined') return
		const mq = window.matchMedia('(min-width: 1024px)')
		if (mq.matches) {
			try {
				inputRef.current?.focus()
			} catch {
				// ignore focus errors
			}
		}
	}, [])

	return (
		<div className="flex flex-col items-center justify-center bg-black">
			<Header description="Begin or resume your application" />
			<form
				className="w-full max-w-lg space-y-4"
				onSubmit={(e) => {
					e.preventDefault()
					setTouched(true)
					if (canSend) onSubmit(email.trim())
				}}
			>
				<div className="flex flex-col gap-3">
					<Input
						label="Email Address"
						type="email"
						inputMode="email"
						maxLength={FIELD_LIMITS.email}
						value={email}
						onValueChange={(val) => {
							setEmail(val)
							setTouched(true)
						}}
						ref={inputRef as any}
						isInvalid={!!displayError}
						errorMessage={displayError || undefined}
						variant="bordered"
					/>
					<Button
						color="primary"
						type="submit"
						isDisabled={!canSend || sending}
						isLoading={sending}
						size="lg"
					>
						<Mail className="size-5" /> Continue with Email
					</Button>
				</div>
			</form>
		</div>
	)
}
